import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FaArrowRight } from 'react-icons/fa';
import emailjs from 'emailjs-com';
import { Section, Input, Button, Card } from '../components/ui';
import Header from './Header';
import Footer from './Footer';
import contactVisual from '../assets/images/about-hero.jpg';
import './ContactPage.css';

const ContactPage = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        description: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
        setSubmitStatus(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            await emailjs.send(
                'service_cpp2p3r',
                'template_9k7gi99',
                {
                    name: formData.name,
                    email: formData.email,
                    description: formData.description
                },
                'sZuR-mMOyFNxwyHyM'
            );
            setSubmitStatus('success');
            setFormData({ name: '', email: '', description: '' });
        } catch (error) {
            setSubmitStatus('error');
            console.error('EmailJS Error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="contact-page-wrapper">
            <Header variant="marketing" />
            <Section variant="large" className="contact-section anchor-target">
                <div className="contact-grid">
                    <motion.div
                        className="contact-copy"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                    >
                        <p className="contact-eyebrow">Contact</p>
                        <h1 className="contact-heading">Tell us about your context.</h1>
                        <p className="contact-subtitle">
                            Consulting, capital, technology, or a blend—we will respond with next steps.
                        </p>

                        <Card variant="glass" className="contact-form-card">
                            <form className="contact-form" onSubmit={handleSubmit}>
                                <Input
                                    type="text"
                                    name="name"
                                    label="Name"
                                    placeholder="Your Name"
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />

                                <Input
                                    type="email"
                                    name="email"
                                    label="Email"
                                    placeholder="your.email@example.com"
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />

                                <div className="contact-textarea-wrapper">
                                    <label htmlFor="description" className="linear-input-label">
                                        Message
                                    </label>
                                    <textarea
                                        id="description"
                                        name="description"
                                        placeholder="Your Message"
                                        value={formData.description}
                                        onChange={handleChange}
                                        className="contact-textarea"
                                        rows={6}
                                        required
                                    />
                                </div>

                                {submitStatus === 'success' && (
                                    <motion.div
                                        className="contact-status contact-status--success"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        Message sent successfully! We'll get back to you soon.
                                    </motion.div>
                                )}

                                {submitStatus === 'error' && (
                                    <motion.div
                                        className="contact-status contact-status--error"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                    >
                                        Failed to send message. Please try again later.
                                    </motion.div>
                                )}

                                <Button
                                    type="submit"
                                    variant="primary"
                                    size="lg"
                                    disabled={isSubmitting}
                                    className="contact-submit-button"
                                >
                                    {isSubmitting ? (
                                        'Sending...'
                                    ) : (
                                        <>
                                            Send Message
                                            <FaArrowRight style={{ marginLeft: '8px' }} />
                                        </>
                                    )}
                                </Button>
                            </form>
                        </Card>
                    </motion.div>

                    <motion.aside
                        className="contact-visual"
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.55, delay: 0.08 }}
                    >
                        <div className="contact-visual-frame">
                            <img
                                src={contactVisual}
                                alt="Glass campus facade and pedestrian plaza in daylight"
                                className="contact-visual-image"
                            />
                        </div>
                        <Card variant="glass" className="contact-aside-card">
                            <h2 className="contact-aside-title">Based in The Hague</h2>
                            <p className="contact-aside-body">
                                MotionSpace BV works with clients across sectors. Reach us here for mandates that span
                                consulting, capital, and technology—or any one of them.
                            </p>
                        </Card>
                    </motion.aside>
                </div>
            </Section>
            <Footer />
        </div>
    );
};

export default ContactPage;
